import { Avatar, Badge, Card, GlobalToken, Space } from "antd";
import type { ChatMessage } from "@libertify.frontend/chatbot";
import { LoadingOutlined } from "@ant-design/icons";
import { useAntTheme } from "hooks/useAntTheme";
import { Suggestion } from "./Suggestion";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import styled from "styled-components";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { AnimatedItem } from "@libertify/ui";

export const Message: React.FC<{
  message: ChatMessage;
  sendQuery: (text: string) => void;
}> = memo(function Message({ message, sendQuery }) {
  const { theme } = useAntTheme();
  const { t } = useTranslation();

  // when undefined/null or true then acts like is finished otherwise it's false
  const isFinal = message.is_final !== false;
  const hasQuery = message.query?.length > 0;
  const hasContent = message?.messages?.[0]?.content?.length > 0;
  const hasMessage = message.message?.length > 0;
  const hasChunk = message.chunk?.length > 0;
  const hasSuggestedQuestions =
    message?.metadata?.suggested_questions?.length > 0;

  // first message and sometimes message comes from messages content
  let messageContent: string;
  if (hasContent) messageContent = message?.messages?.[0]?.content;
  else if (hasMessage) messageContent = message.message;
  else messageContent = "";

  return (
    <Card
      title={hasQuery ? message.query : null}
      variant="borderless"
      style={{
        padding: 0,
        background: "none",
        boxShadow: "none",
      }}
      styles={{
        body: {
          padding: 0,
          border: 0,
          whiteSpace: "pre-wrap",
          position: "relative",
        },
        header: { border: 0 },
        title: {
          overflow: "visible",
          whiteSpace: "normal",
          marginLeft: -24,
          marginBottom: 10,
          fontSize: 18,
          fontWeight: "normal",
        },
      }}
    >
      {/* {htmlContent} */}
      <StyledSpace align="start">
        {(hasMessage || hasContent) && (
          <Avatar
            style={{ backgroundColor: theme.colorPrimary }}
            size={"small"}
          >
            {t("components.chat.Message.ai")}
          </Avatar>
        )}
        <div style={{ position: "absolute", top: 0, left: 0 }}>
          {!isFinal && <LoadingOutlined spin style={{ fontSize: 24 }} />}
        </div>
        <div>
          <Markdown remarkPlugins={[remarkGfm]}>
            {messageContent?.replaceAll(/<br\/?>/gi, "\n")}
          </Markdown>
          {!isFinal && hasChunk && (
            <Badge dot color={theme.colorText} style={{ marginLeft: 10 }} />
          )}
          {hasSuggestedQuestions && (
            <>
              <p style={{ fontWeight: 500, margin: "10px 0" }}>
                {t("components.chat.Message.followUpQuestions")}
              </p>
              <div
                style={{
                  gap: 8,
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: 20,
                }}
              >
                {message?.metadata?.suggested_questions?.map(
                  (suggestion, index) => (
                    <AnimatedItem key={index} $delay={index * 100}>
                      <Suggestion question={suggestion} onSelect={sendQuery} />
                    </AnimatedItem>
                  )
                )}
              </div>
            </>
          )}
        </div>
      </StyledSpace>
    </Card>
  );
});

const StyledSpace = styled(Space)<{
  theme: GlobalToken;
}>`
  ul,
  ol {
    margin-top: 1em;
    margin-bottom: 0;
    padding-left: 1em;
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    li::marker {
      /* todo */
    }
  }

  p {
    margin: 0;
  }

  /* prevent line break at start of message */
  p:first-child {
    display: inline;
  }

  /* prevent line break at end of message */
  div {
    display: inline;
  }

  table {
    border-collapse: collapse;
    margin: 0.8em 0;
    border: 1px solid ${(props) => props.theme.colorBorder}99;

    thead tr {
      background-color: ${(props) => props.theme.colorBgContainer};
      text-align: left;
    }
    th,
    td {
      padding: 12px 10px;
    }
    td {
      border: 1px solid ${(props) => props.theme.colorBorder}99;
    }
  }
`;
